import _ from 'lodash';

import { getConditionalObject } from 'utils/data';
import { clearEntitySideDataFromEmptyData } from 'utils/loanApplication';

export const mergeValues = ({ form, initialValues, isFormDirty }) => {
  const ids = Object.keys(initialValues);

  return ids.reduce((acc, id) => {
    const clonedEntityInitialValues = _.cloneDeep(initialValues[id]);
    const mergedValues = _.merge(clonedEntityInitialValues, form[id]);

    acc[id] = {
      ...mergedValues,
      ...getConditionalObject('owners' in clonedEntityInitialValues && isFormDirty, {
        owners: form[id]?.owners ?? mergedValues.owners,
      }),
    };

    return acc;
  }, {});
};

export const mergeDataWithPendingChanges = (data, pendingChanges) => {
  if (!pendingChanges || !data || !Object.keys(data).length) {
    return data;
  }

  return Object.entries(data).reduce((acc, [recordId, recordData]) => {
    const recordPendingChanges = pendingChanges[recordId];

    if (!recordPendingChanges) {
      acc[recordId] = recordData;

      return acc;
    }

    const pendingFieldValues = Object.entries(recordPendingChanges).reduce(
      (fields, [fieldName, pendingChange]) => {
        if (fieldName === 'owners' || fieldName === 'pendingChangesActionType') {
          return fields;
        }

        if (recordData[fieldName] == null && pendingChange?.newValue != null) {
          fields[fieldName] = pendingChange.newValue;
        }

        return fields;
      },
      {},
    );

    acc[recordId] = Object.keys(pendingFieldValues).length
      ? { ...recordData, ...pendingFieldValues }
      : recordData;

    return acc;
  }, {});
};

export const clearEntitiesSideDataFromEmptyData = entitiesSideData => {
  return _.mapValues(entitiesSideData, entitySideData =>
    clearEntitySideDataFromEmptyData(entitySideData),
  );
};

const getChildErrorPath = ({ parentPath, key, isArray }) => {
  if (isArray) {
    return `${parentPath}[${key}]`;
  }

  return parentPath ? `${parentPath}.${key}` : key;
};

const collectErrorLeaves = (value, path) => {
  if (_.isPlainObject(value) || _.isArray(value)) {
    const isArray = _.isArray(value);

    return Object.entries(value).flatMap(([key, child]) =>
      collectErrorLeaves(child, getChildErrorPath({ parentPath: path, key, isArray })),
    );
  }

  if (!value) {
    return [];
  }

  return [{ path, message: value }];
};

export const flattenErrorLeaves = errors =>
  Object.entries(errors ?? {}).flatMap(([recordId, recordErrors]) =>
    collectErrorLeaves(recordErrors, '').map(leaf => ({ recordId, ...leaf })),
  );

export const getRecordOwnerIds = ({ form, data, recordId }) => {
  const currentOwners = form?.[recordId]?.owners ?? form?.[recordId]?.owner;
  const savedOwners = data?.[recordId]?.owners ?? data?.[recordId]?.owner;

  return _.castArray(currentOwners ?? savedOwners ?? [])
    .map(owner => owner?.value ?? owner)
    .filter(Boolean);
};

export const getValidationErrorSummary = ({ errors, form, data, deletedRecordIds }) => {
  const deletedIds = new Set(deletedRecordIds);
  const errorCounts = _.countBy(flattenErrorLeaves(errors), 'recordId');

  return Object.entries(errorCounts).reduce((acc, [recordId, count]) => {
    if (!deletedIds.has(recordId)) {
      acc[recordId] = { count, ownerIds: getRecordOwnerIds({ form, data, recordId }) };
    }

    return acc;
  }, {});
};

const getFieldLabel = ({ fieldsConfig, recordId, path }) => {
  const pathSegments = _.toPath(path);
  const label = _.get(fieldsConfig, [recordId, ...pathSegments])?.label;

  if (_.isString(label)) {
    return label;
  }

  const lastNamedSegment = _.findLast(pathSegments, segment => !/^\d+$/.test(segment)) ?? '';

  return _.startCase(lastNamedSegment);
};

const buildSaveBlockedPayload = ({ errors, fieldsConfig }) =>
  flattenErrorLeaves(errors).map(({ recordId, path, message }) => ({
    recordId,
    path,
    message,
    label: getFieldLabel({ fieldsConfig, recordId, path }),
  }));

export const runSaveValidation = async ({
  validateForm,
  validationContext,
  getErrors,
  fieldsConfig,
  onSaveBlocked,
}) => {
  const isValid = await validateForm(validationContext);

  if (!isValid) {
    onSaveBlocked(buildSaveBlockedPayload({ errors: getErrors(), fieldsConfig }));
  }

  return isValid;
};
